/**
 * Eagle DTF Supply — Core JavaScript
 * Version: 1.0.0
 *
 * Custom Shopify theme JS — zero external dependencies.
 * Handles: scroll animations, cart drawer, mobile nav,
 * back-to-top, modals, search, tabs, accordions,
 * header scroll effects, quantity selectors.
 */

(function () {
  'use strict';

  /* ══════════════════════════════════════════════════
     1. Scroll Animations (IntersectionObserver)
     ══════════════════════════════════════════════════ */
  function initScrollAnimations() {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion || document.body.hasAttribute('data-reduce-animations')) {
      // Show all elements immediately
      document.querySelectorAll('[data-animate]').forEach(el => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0, rootMargin: '0px 0px 60px 0px' }
    );

    document.querySelectorAll('[data-animate]').forEach(el => observer.observe(el));

    // Fallback: images loading can shift layout after observer fires; force-reveal any missed elements
    window.addEventListener('load', () => {
      setTimeout(() => {
        document.querySelectorAll('[data-animate]:not(.is-visible)').forEach(el => {
          el.classList.add('is-visible');
        });
      }, 800);
    }, { once: true });

    // Stagger children
    document.querySelectorAll('[data-stagger]').forEach(container => {
      Array.from(container.children).forEach((child, i) => {
        child.style.setProperty('--stagger-index', i);
      });
    });
  }

  /* ══════════════════════════════════════════════════
     2. Header Scroll Effect
     ══════════════════════════════════════════════════ */
  function initHeaderScroll() {
    const header = document.querySelector('.site-header');
    if (!header) return;

    let lastScroll = 0;
    let ticking = false;

    function onScroll() {
      const currentScroll = window.pageYOffset;

      if (currentScroll > 60) {
        header.classList.add('site-header--scrolled');
      } else {
        header.classList.remove('site-header--scrolled');
      }

      lastScroll = currentScroll;
      ticking = false;
    }

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(onScroll);
        ticking = true;
      }
    }, { passive: true });
  }

  /* ══════════════════════════════════════════════════
     3. Cart Drawer — DISABLED: now handled by eagle-cart-drawer.liquid snippet
     ══════════════════════════════════════════════════ */
  function initCartDrawer() {
    // Cart drawer is now managed by snippets/eagle-cart-drawer.liquid
    // This function is intentionally empty to prevent conflicts
    return;
  }

  /* ══════════════════════════════════════════════════
     4. Mobile Navigation
     ══════════════════════════════════════════════════ */
  function initMobileNav() {
    const toggle = document.querySelector('[data-mobile-menu-toggle]');
    const nav = document.querySelector('[data-mobile-nav]');
    if (!toggle || !nav) return;

    const overlay = nav.querySelector('.mobile-nav__overlay');

    function initTopbarClone() {
      const topbar = document.querySelector('.announcement-left');
      const mobileNavScroll = nav.querySelector('.mobile-nav__scroll');
      if (topbar && mobileNavScroll && !nav.querySelector('.injected-topbar')) {
        const clone = document.createElement('div');
        clone.className = 'injected-topbar mobile-nav__info-bar';
        clone.innerHTML = topbar.innerHTML;
        // Append to the bottom of the scroll area (after regular menu links)
        mobileNavScroll.appendChild(clone);
      }
    }

    function openNav() {
      initTopbarClone();
      nav.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    }

    function closeNav() {
      nav.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }

    toggle.addEventListener('click', () => {
      nav.classList.contains('is-open') ? closeNav() : openNav();
    });

    if (overlay) overlay.addEventListener('click', closeNav);

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && nav.classList.contains('is-open')) closeNav();
    });
  }

  /* ══════════════════════════════════════════════════
     5. Back to Top Button
     ══════════════════════════════════════════════════ */
  function initBackToTop() {
    const btn = document.querySelector('[data-back-to-top]');
    if (!btn) return;

    let ticking = false;

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          btn.classList.toggle('is-visible', window.pageYOffset > 400);
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });

    btn.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  /* ══════════════════════════════════════════════════
     6. Modal System
     ══════════════════════════════════════════════════ */
  function initModals() {
    const modals = document.querySelectorAll('[data-modal]');

    modals.forEach(modal => {
      const closeBtns = modal.querySelectorAll('[data-modal-close]');
      closeBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          modal.setAttribute('aria-hidden', 'true');
          document.body.style.overflow = '';
        });
      });

      // Close on overlay click
      modal.addEventListener('click', e => {
        if (e.target === modal) {
          modal.setAttribute('aria-hidden', 'true');
          document.body.style.overflow = '';
        }
      });
    });

    // Global open modal function
    window.EagleTheme = window.EagleTheme || {};
    window.EagleTheme.openModal = function (modalId, content) {
      const modal = document.getElementById(modalId);
      if (!modal) return;

      if (content) {
        const body = modal.querySelector('[data-modal-content]');
        if (body) body.innerHTML = content;
      }

      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    };

    window.EagleTheme.closeModal = function (modalId) {
      const modal = document.getElementById(modalId);
      if (!modal) return;
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    };

    // Escape key for all modals
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') {
        modals.forEach(modal => {
          if (modal.getAttribute('aria-hidden') === 'false') {
            modal.setAttribute('aria-hidden', 'true');
            document.body.style.overflow = '';
          }
        });
      }
    });
  }

  /* ══════════════════════════════════════════════════
     7. Rich Predictive Search (Zero-Lag JSON API)
     ══════════════════════════════════════════════════ */
  function initSearch() {
    const container = document.querySelector('[data-predictive-search]');
    if (!container) return;

    const input = container.querySelector('[data-predictive-search-input]');
    const resultsContainer = container.querySelector('[data-predictive-search-results]');
    const spinner = container.querySelector('[data-search-spinner]');
    const form = container.querySelector('form');
    let debounceTimer;

    function openResults() {
      resultsContainer.style.display = 'block';
    }

    function closeResults() {
      resultsContainer.style.display = 'none';
      if (spinner) spinner.style.display = 'none';
    }

    function renderResults(results) {
      const products = results.products || [];
      if (products.length === 0) {
        resultsContainer.innerHTML = `<div class="predictive-search__empty">No results found</div>`;
        openResults();
        return;
      }

      let html = `<ul class="predictive-search__list">`;
      // Shopify's native JSON suggest endpoint returns objects with thumbnail formats.
      products.forEach(p => {
        html += `
          <li class="predictive-search__item">
            <a href="${p.url}" class="predictive-search__link">
              ${p.featured_image ? `<img src="${p.featured_image.url || p.image}" alt="${p.title}" class="predictive-search__image">` : `<img src="${p.image}" alt="${p.title}" class="predictive-search__image">`}
              <div class="predictive-search__info">
                <span class="predictive-search__title">${p.title}</span>
                <span class="predictive-search__price">${p.price}</span>
              </div>
            </a>
          </li>
        `;
      });
      html += `</ul><button type="button" class="predictive-search__view-all" onclick="this.closest('form').submit()">View All Results →</button>`;
      
      resultsContainer.innerHTML = html;
      // Add form submit bindings for view all button manually since it is outside form
      resultsContainer.querySelector('.predictive-search__view-all').addEventListener('click', () => form.submit());
      openResults();
    }

    input.addEventListener('input', () => {
      const query = input.value.trim();
      if (query.length < 2) {
        closeResults();
        clearTimeout(debounceTimer);
        return;
      }

      if (spinner) spinner.style.display = 'block';
      clearTimeout(debounceTimer);

      debounceTimer = setTimeout(async () => {
        try {
          const res = await fetch(`${window.EagleTheme.routes.predictiveSearch}?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=5`);
          const json = await res.json();
          renderResults(json.resources.results);
        } catch (err) {
          console.error('Search fetch failed:', err);
        } finally {
          if (spinner) spinner.style.display = 'none';
        }
      }, 300);
    });

    input.addEventListener('focus', () => {
      if (input.value.trim().length >= 2 && resultsContainer.innerHTML.includes('predictive-search__list')) {
        openResults();
      }
    });

    document.addEventListener('click', (e) => {
      if (!container.contains(e.target)) closeResults();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeResults();
    });
  }

  /* ══════════════════════════════════════════════════
     8. Accordions
     ══════════════════════════════════════════════════ */
  function initAccordions() {
    document.querySelectorAll('.accordion__trigger').forEach(trigger => {
      trigger.addEventListener('click', () => {
        const item = trigger.closest('.accordion__item');
        if (!item) return;

        const isOpen = item.classList.contains('is-open');

        // Close siblings (optional)
        const parent = item.parentElement;
        if (parent && parent.hasAttribute('data-accordion-single')) {
          parent.querySelectorAll('.accordion__item').forEach(sibling => {
            sibling.classList.remove('is-open');
            const content = sibling.querySelector('.accordion__content');
            if (content) content.style.maxHeight = '0';
          });
        }

        // Toggle current
        item.classList.toggle('is-open', !isOpen);
        const content = item.querySelector('.accordion__content');
        if (content) {
          content.style.maxHeight = isOpen ? '0' : content.scrollHeight + 'px';
        }

        // Update ARIA
        trigger.setAttribute('aria-expanded', !isOpen);
      });
    });
  }

  /* ══════════════════════════════════════════════════
     9. Tabs
     ══════════════════════════════════════════════════ */
  function initTabs() {
    document.querySelectorAll('[data-tabs]').forEach(tabsContainer => {
      const tabs = tabsContainer.querySelectorAll('[data-tab]');
      const panels = tabsContainer.querySelectorAll('[data-tab-panel]');

      tabs.forEach(tab => {
        tab.addEventListener('click', () => {
          const target = tab.getAttribute('data-tab');

          tabs.forEach(t => t.classList.remove('is-active'));
          panels.forEach(p => p.classList.remove('is-active'));

          tab.classList.add('is-active');
          const panel = tabsContainer.querySelector(`[data-tab-panel="${target}"]`);
          if (panel) panel.classList.add('is-active');
        });
      });
    });
  }

  /* ══════════════════════════════════════════════════
     10. Quantity Selectors
     ══════════════════════════════════════════════════ */
  function initQuantitySelectors() {
    document.querySelectorAll('.quantity-selector').forEach(selector => {
      const input = selector.querySelector('.quantity-selector__input');
      const minus = selector.querySelector('[data-qty-minus]');
      const plus = selector.querySelector('[data-qty-plus]');

      if (!input || !minus || !plus) return;

      const min = parseInt(input.getAttribute('min') || '1', 10);
      const max = parseInt(input.getAttribute('max') || '999', 10);

      minus.addEventListener('click', () => {
        let val = parseInt(input.value, 10) || min;
        if (val > min) {
          input.value = val - 1;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      plus.addEventListener('click', () => {
        let val = parseInt(input.value, 10) || min;
        if (val < max) {
          input.value = val + 1;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      input.addEventListener('change', () => {
        let val = parseInt(input.value, 10);
        if (isNaN(val) || val < min) input.value = min;
        if (val > max) input.value = max;
      });
    });
  }

  /* ══════════════════════════════════════════════════
     11. AJAX Cart API
     ══════════════════════════════════════════════════ */
  window.EagleTheme = window.EagleTheme || {};

  window.EagleTheme.cart = {
    async get() {
      const res = await fetch('/cart.js');
      return res.json();
    },

    _addLock: false,
    async add(variantId, quantity = 1, properties = {}) {
      if (window.EagleTheme.cart._addLock) return Promise.reject('Add to Cart in progress');
      window.EagleTheme.cart._addLock = true;
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: variantId, quantity, properties }]
          })
        });
        if (!res.ok) throw new Error('Failed to add to cart');
        const data = await res.json();
        window.EagleTheme.cart.updateCount();
        window.EagleTheme.showToast('Added to cart!', 'success');
        return data;
      } finally {
        window.EagleTheme.cart._addLock = false;
      }
    },

    async update(updates) {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });
      if (!res.ok) throw new Error('Failed to update cart');
      const data = await res.json();
      window.EagleTheme.cart.updateCount();
      return data;
    },

    async change(line, quantity) {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      if (!res.ok) throw new Error('Failed to change cart');
      const data = await res.json();
      window.EagleTheme.cart.updateCount();
      return data;
    },

    async updateCount() {
      try {
        const cart = await this.get();
        const countEls = document.querySelectorAll('[data-cart-count]');
        countEls.forEach(el => {
          el.textContent = cart.item_count;
          el.style.display = cart.item_count > 0 ? '' : 'none';
        });
      } catch (e) {
        console.warn('Cart count update failed:', e);
      }
    }
  };

  /* ══════════════════════════════════════════════════
     12. Toast Notifications
     ══════════════════════════════════════════════════ */
  window.EagleTheme.showToast = function (message, type = 'success', duration = 3000) {
    // Remove existing toasts
    document.querySelectorAll('.toast').forEach(t => t.remove());

    const toast = document.createElement('div');
    toast.className = `toast toast--${type}`;

    const iconSvg = type === 'success'
      ? '<svg class="toast__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>'
      : '<svg class="toast__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>';

    toast.innerHTML = `${iconSvg}<span>${message}</span>`;
    document.body.appendChild(toast);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        toast.classList.add('is-visible');
      });
    });

    setTimeout(() => {
      toast.classList.remove('is-visible');
      setTimeout(() => toast.remove(), 400);
    }, duration);
  };

  /* ══════════════════════════════════════════════════
     12b. Tab Switching (Product Page)
     ══════════════════════════════════════════════════ */
  window.EagleTheme.switchTab = function (btn, panelId) {
    // Deactivate all tabs in the same nav
    const nav = btn.closest('.product-tabs__nav');
    if (nav) nav.querySelectorAll('.product-tabs__tab').forEach(t => t.classList.remove('is-active'));
    btn.classList.add('is-active');

    // Deactivate all panels in the same container
    const container = btn.closest('.product-tabs');
    if (container) {
      container.querySelectorAll('.product-tabs__panel').forEach(p => p.classList.remove('is-active'));
      const panel = document.getElementById(panelId);
      if (panel) panel.classList.add('is-active');
    }
  };

  /* ══════════════════════════════════════════════════
     13. Dropdown Navigation
     ══════════════════════════════════════════════════ */
  function initDropdowns() {
    document.querySelectorAll('[data-dropdown-trigger]').forEach(trigger => {
      const dropdown = trigger.nextElementSibling;
      if (!dropdown || !dropdown.classList.contains('nav-dropdown')) return;

      let timeout;

      trigger.addEventListener('mouseenter', () => {
        clearTimeout(timeout);
        // Close other dropdowns
        document.querySelectorAll('.nav-dropdown.is-open').forEach(d => {
          if (d !== dropdown) d.classList.remove('is-open');
        });
        dropdown.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
      });

      const parent = trigger.parentElement;
      parent.addEventListener('mouseleave', () => {
        timeout = setTimeout(() => {
          dropdown.classList.remove('is-open');
          trigger.setAttribute('aria-expanded', 'false');
        }, 100);
      });

      dropdown.addEventListener('mouseenter', () => clearTimeout(timeout));
    });
  }

  /* ══════════════════════════════════════════════════
     14. Color Mode
     ══════════════════════════════════════════════════ */
  function initColorMode() {
    const body = document.body;
    const mode = body.getAttribute('data-color-mode');

    if (mode === 'auto') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)');
      body.setAttribute('data-color-mode', prefersDark.matches ? 'dark' : 'light');
      prefersDark.addEventListener('change', e => {
        body.setAttribute('data-color-mode', e.matches ? 'dark' : 'light');
      });
    }
  }

  /* ══════════════════════════════════════════════════
     15. Product Form (Variant Change — enhanced for 2026 swatches)
     ══════════════════════════════════════════════════ */
  function initProductForms() {
    document.querySelectorAll('[data-product-form]').forEach(form => {
      const variantSelect = form.querySelector('[data-variant-select]');
      if (!variantSelect) return;

      // Handle ALL swatch/button types: .swatch, .variant-color-swatch, .variant-size-btn
      const allSwatches = form.querySelectorAll('.swatch, .variant-color-swatch, .variant-size-btn');
      allSwatches.forEach(swatch => {
        swatch.addEventListener('click', () => {
          // Find the parent group and deselect siblings
          const parent = swatch.closest('.variant-color-swatches, .variant-size-buttons, .swatches');
          if (parent) parent.querySelectorAll('.is-selected').forEach(s => s.classList.remove('is-selected'));
          swatch.classList.add('is-selected');

          // Update hidden select for option group
          const optionIndex = swatch.getAttribute('data-option-index');
          const optionValue = swatch.getAttribute('data-value');
          if (optionIndex !== null && optionValue) {
            const selects = form.querySelectorAll('select[data-option]');
            if (selects[optionIndex]) {
              selects[optionIndex].value = optionValue;
              selects[optionIndex].dispatchEvent(new Event('change', { bubbles: true }));
            }

            // Update the label showing selected value
            const group = swatch.closest('.variant-option-group');
            if (group) {
              const selectedLabel = group.querySelector('.selected-value');
              if (selectedLabel) selectedLabel.textContent = '— ' + optionValue;
            }
          }

          // Varyant id güncelleme
          const selects = form.querySelectorAll('select[data-option]');
          const selectedOptions = Array.from(selects).map(s => s.value);
          if (window.EagleTheme && window.EagleTheme.productVariants) {
            const variant = window.EagleTheme.productVariants.find(v => {
              return v.options && v.options.length === selectedOptions.length && v.options.every((opt, idx) => opt === selectedOptions[idx]);
            });
            if (variant) {
              const variantInput = form.querySelector('[data-variant-select]');
              if (variantInput) variantInput.value = variant.id;
            }
          }
        });
      });

      // Add to Cart submit handler
      form.addEventListener('submit', async function(e) {
        e.preventDefault();
        const variantId = form.querySelector('[data-variant-select]')?.value;
        const qtyInput = form.querySelector('input[name="quantity"]');
        const quantity = qtyInput ? parseInt(qtyInput.value, 10) : 1;
        if (!variantId) {
          window.EagleTheme.showToast('Please select a variant.', 'error');
          return;
        }
        try {
          await window.EagleTheme.cart.add(variantId, quantity);
        } catch (err) {
          window.EagleTheme.showToast('Failed to add to cart.', 'error');
        }
      });
    });
  }

  /* ══════════════════════════════════════════════════
     INIT — Run everything on DOM ready
     ══════════════════════════════════════════════════ */
  function init() {
    initColorMode();
    initScrollAnimations();
    initHeaderScroll();
    initCartDrawer();
    initMobileNav();
    initBackToTop();
    initModals();
    initSearch();
    initAccordions();
    initTabs();
    initQuantitySelectors();
    initDropdowns();
    initProductForms();

    // Update cart count on load
    if (window.EagleTheme && window.EagleTheme.cart) {
      window.EagleTheme.cart.updateCount();
    }
  }

  // DOM Ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize on Shopify section render (theme editor)
  document.addEventListener('shopify:section:load', () => {
    initScrollAnimations();
    initAccordions();
    initTabs();
    initQuantitySelectors();
    initProductForms();
  });
})();



